/**
 * Tracks a trigger element against the viewport and toggles sticky classes
 * for primary/secondary product UI elements.
 *
 * Behavior summary:
 * - Activates only when viewport width is <= `maxWidth`.
 * - Shows or hides sticky UI based on tracked element position (`visible|above|below`).
 * - Updates CSS variables for stacked sticky positioning.
 * - Detects when the secondary sticky element is physically pinned and toggles `priceStickyClass`.
 * - Handles scroll, resize, orientation changes, and dynamic option-block clicks.
 */
class ViewportTracker {
  constructor(options) {
    if (!options || !options.trackedElement || !options.stickyElement) {
      throw new Error('trackedElement and stickyElement must be provided');
    }
   
    this.onScroll = null;

    this.trackedElement = options.trackedElement;
    this.stickyElement = options.stickyElement;
    this.secondaryStickyElement = options.secondaryStickyElement || null;
    this.stickyState = options.initialStickyState || 'visible';
    this.throttleDelay = options.throttleDelay || 16;
    this.resizeDebounceDelay = options.resizeDebounceDelay || 250;
    this.offset = options.offset || 0;
    this.maxWidth = options.maxWidth || Infinity;
    this.secondarySpacing = options.secondarySpacing || 0;
    this.liveOptionsSelector = options.liveOptionsSelector || '[id*="live_product_options_app"]';
    this.liveOptionsClickTimeout = null;
    this.resizeCompleteTimeout = null;

    // CSS class names
    this.classes = {
      stickyActive: options.stickyActiveClass || 'viewport-tracker-sticky',
      stickyHidden: options.stickyHiddenClass || 'viewport-tracker-hidden',
      stickyVisible: options.stickyVisibleClass || 'viewport-tracker-visible',
      secondaryActive: options.secondaryActiveClass || 'viewport-tracker-secondary',
      priceSticky: options.priceStickyClass || 'price-is-sticky', // NEW: Class for when price is physically stuck
      inactive: options.inactiveClass || 'viewport-tracker-inactive',
      resizing: options.resizingClass || 'viewport-tracker-resizing'
    };

    // Resize handling state
    this.currentViewportWidth = 0;
    this.currentOrientation = this.getOrientation();
    this.isResizing = false;
    this.resizeTimeouts = new Set();
    
    // Tracking state
    this.lastPosition = null;
    this.ticking = false;
    this.isActive = false;
    this.wasActiveBeforeResize = false;
    this.currentStickyState = null; // Track current visual sticky state

    // NEW: Track previous positions for sticky detection
    this.lastPrimaryTop = null;
    this.lastSecondaryTop = null;
    this.isPrimaryStuck = false;
    this.isSecondaryStuck = false;

    this.init();
  }

  init() {
  // Bind once
  this.updateState = this.updateState.bind(this);
  this.handleResize = this.handleResize.bind(this);
  this.handleOrientationChange = this.handleOrientationChange.bind(this);
  this.handleLiveOptionsClick = this.handleLiveOptionsClick.bind(this);

  // Store initial viewport state and whether tracker is active
  this.currentViewportWidth = this.getViewportWidth();
  this.isActive = this.currentViewportWidth <= this.maxWidth;
  this.currentOrientation = this.getOrientation();

  // Prime first-frame sticky detection (avoid early-return on first check)
  const pRect = this.stickyElement.getBoundingClientRect();
  this.lastPrimaryTop = pRect.top;
  if (this.secondaryStickyElement) {
    const sRect = this.secondaryStickyElement.getBoundingClientRect();
    this.lastSecondaryTop = sRect.top;
  } else {
    this.lastSecondaryTop = 0;
  }

  // Scroll listener (store exact ref for destroy)
  this.onScroll = this.throttle(this.updateState, this.throttleDelay);
  window.addEventListener('scroll', this.onScroll, { passive: true });

  // Resize/orientation listeners
  window.addEventListener('resize', this.handleResize);
  window.addEventListener('orientationchange', this.handleOrientationChange);
  if ('visualViewport' in window) {
    window.visualViewport.addEventListener('resize', this.handleResize);
  }

  // Listen for option clicks in dynamically-generated Live Product Options block.
  document.addEventListener('click', this.handleLiveOptionsClick, true);

    // Guard against layout shifts (images, fonts, accordions, etc.)
    if ('ResizeObserver' in window) {
      this.layoutRO = new ResizeObserver(() => {
        this.checkActualStickyState();
      });
      const roTarget = this.stickyElement.offsetParent || document.documentElement;
      this.layoutRO.observe(roTarget);
      this.layoutRO.observe(this.stickyElement);
      if (this.secondaryStickyElement) this.layoutRO.observe(this.secondaryStickyElement);
    }

    // Late-loading assets (e.g., images) — re-check stickiness
    this.assetLoadHandler = this.debounce(() => this.checkActualStickyState(), 50);
    document.addEventListener('load', this.assetLoadHandler, true);

    // Initial classes/state
    this.setupInitialState();
    this.updateState();
  }

  getStickyTop(el) {
  const t = parseFloat(getComputedStyle(el).top);
    return Number.isNaN(t) ? 0 : t;
  }

  handleLiveOptionsClick(event) {
    if (!event.target || !(event.target instanceof Element)) return;

    const clickedInsideLiveOptions = event.target.closest(this.liveOptionsSelector);
    if (!clickedInsideLiveOptions) return;

    this.slideGalleryToFirstImage();

    // Immediate refresh for sync DOM updates.
    if (this.isActive) {
      this.forceUpdateState();
    }
    this.checkActualStickyState();

    // Follow-up refresh for async app block updates after the click.
    if (this.liveOptionsClickTimeout) {
      clearTimeout(this.liveOptionsClickTimeout);
    }
    this.liveOptionsClickTimeout = setTimeout(() => {
      this.slideGalleryToFirstImage();
      if (this.isActive) {
        this.forceUpdateState();
      }
      this.checkActualStickyState();
    }, 120);
  }

  slideGalleryToFirstImage() {
    const gallery = document.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

    const firstSlide = gallery.querySelector('[id^="Slider-Gallery"] [data-media-id]');
    if (!firstSlide || !firstSlide.dataset || !firstSlide.dataset.mediaId) return;

    gallery.setActiveMedia(firstSlide.dataset.mediaId, false);
  }

  getViewportWidth() {
    return window.innerWidth || document.documentElement.clientWidth;
  }

  getOrientation() {
    const screenOrientation = window.screen && window.screen.orientation;
    if (screenOrientation && typeof screenOrientation.angle === 'number') {
      return screenOrientation.angle;
    }
    if (window.orientation !== undefined) {
      return window.orientation;
    }
    return 0;
  }

  setupInitialState() {
    this.setupInitialClasses();
  }

  setupInitialClasses() {
    if (this.isActive) {
      // Mobile: Add hidden classes for sticky behavior
      this.stickyElement.classList.add(this.classes.stickyHidden);
      
      if (this.secondaryStickyElement) {
        this.secondaryStickyElement.classList.add(this.classes.stickyHidden);
      }
    } else {
      // Desktop: Add inactive classes
      this.stickyElement.classList.add(this.classes.inactive);
      
      if (this.secondaryStickyElement) {
        this.secondaryStickyElement.classList.add(this.classes.inactive);
      }
    }
  }

  throttle(func, delay) {
    let lastCall = 0;
    const self = this;
    return function (...args) {
      const now = performance.now();
      if (now - lastCall < delay) return;
      lastCall = now;
      return func.apply(self, args);
    };
  }

  debounce(func, delay) {
    let timeoutId;
    const self = this;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(self, args), delay);
      return timeoutId;
    };
  }

  handleResize() {
    const newWidth = this.getViewportWidth();
    const newOrientation = this.getOrientation();
    
    // Check if this is a significant resize (not just scrollbar changes)
    const significantWidthChange = Math.abs(newWidth - this.currentViewportWidth) > 10;
    const orientationChanged = newOrientation !== this.currentOrientation;
    
    if (!significantWidthChange && !orientationChanged) {
      return;
    }

    // Store previous state for smooth transitions
    this.wasActiveBeforeResize = this.isActive;
    
    // Start resize handling
    this.startResizeMode();
    
    // Update current values
    this.currentViewportWidth = newWidth;
    this.currentOrientation = newOrientation;

    // Immediately check and apply new viewport state
    this.applyImmediateResizeState();

    // Debounced resize completion handler
    if (this.resizeCompleteTimeout) {
      clearTimeout(this.resizeCompleteTimeout);
      this.resizeTimeouts.delete(this.resizeCompleteTimeout);
    }
    this.resizeCompleteTimeout = setTimeout(() => {
      this.completeResize();
    }, this.resizeDebounceDelay);
    this.resizeTimeouts.add(this.resizeCompleteTimeout);
  }

  handleOrientationChange() {
    // Orientation changes need special handling with longer delay
    this.startResizeMode();
    
    const timeoutId = setTimeout(() => {
      this.currentViewportWidth = this.getViewportWidth();
      this.currentOrientation = this.getOrientation();
      this.applyImmediateResizeState();
      this.completeResize();
    }, 500); // Longer delay for orientation changes
    
    this.resizeTimeouts.add(timeoutId);
  }

  applyImmediateResizeState() {
    // Determine if tracker should be active with new viewport
    const shouldBeActive = this.currentViewportWidth <= this.maxWidth;
    
    if (shouldBeActive !== this.wasActiveBeforeResize) {
      // Viewport crossed the threshold - immediate state change needed
      if (!shouldBeActive) {
        // Switched to desktop - immediately hide and deactivate
        this.forceHideElements();
        this.isActive = false;
      } else {
        // Switched to mobile - activate but respect current scroll position
        this.isActive = true;
        this.activate();
        // Force update state based on current trigger position
        this.forceUpdateState();
      }
    } else if (shouldBeActive) {
      // Still mobile, just update measurements
      this.isActive = true;
      this.updateSecondaryPosition();
    }
  }

  forceHideElements() {
    // Immediately hide sticky elements regardless of trigger position
    this.stickyElement.classList.remove(
      this.classes.stickyActive, 
      this.classes.stickyVisible
    );
    this.stickyElement.classList.add(this.classes.stickyHidden, this.classes.inactive);

    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.classList.remove(
        this.classes.secondaryActive,
        this.classes.stickyVisible,
        this.classes.priceSticky // NEW: Remove price sticky class
      );
      this.secondaryStickyElement.classList.add(this.classes.stickyHidden, this.classes.inactive);
    }

    document.documentElement.style.removeProperty('--primary-sticky-height');
    this.currentStickyState = 'hidden';

    // NEW: Reset sticky detection state
    this.resetStickyDetection();
  }

  forceUpdateState() {
    // Force an immediate state update regardless of throttling
    const position = this.getElementPosition();
    this.lastPosition = position;
    this.toggleStickyPosition(position);
  }

  startResizeMode() {
    if (this.isResizing) return;
    
    this.isResizing = true;
    
    // Add resizing class to prevent awkward transitions
    this.stickyElement.classList.add(this.classes.resizing);
    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.classList.add(this.classes.resizing);
    }
    
    // Temporarily disable transitions during resize
    document.documentElement.style.setProperty('--resize-mode', '1');

    // NEW: Reset sticky detection during resize
    this.resetStickyDetection();
  }

  completeResize() {
    // Clear any pending timeouts
    this.resizeTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.resizeTimeouts.clear();
    this.resizeCompleteTimeout = null;
    
    // Remove resizing state
    this.isResizing = false;
    this.stickyElement.classList.remove(this.classes.resizing);
    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.classList.remove(this.classes.resizing);
    }
    
    // Re-enable transitions
    document.documentElement.style.removeProperty('--resize-mode');
    
    // Final state check and update
    this.checkViewportWidth();
    
    // Force a final state update to ensure everything is correct
    if (this.isActive) {
      this.forceUpdateState();
      
      // Recalculate secondary positioning if elements are currently sticky
      if (this.secondaryStickyElement && 
          this.secondaryStickyElement.classList.contains(this.classes.secondaryActive)) {
        setTimeout(() => this.updateSecondaryPosition(), 100);
      }
    }
  }

  checkViewportWidth() {
    this.currentViewportWidth = this.getViewportWidth();
    const shouldBeActive = this.currentViewportWidth <= this.maxWidth;
    
    if (shouldBeActive !== this.isActive) {
      // State change needed
      this.isActive = shouldBeActive;
      
      if (shouldBeActive) {
        this.activate();
        this.forceUpdateState();
      } else {
        this.forceHideElements();
        this.deactivate();
      }
    }
  }

  activate() {
    // Remove inactive class and add initial hidden state for mobile
    this.stickyElement.classList.remove(this.classes.inactive);
    this.stickyElement.classList.add(this.classes.stickyHidden);
    
    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.classList.remove(this.classes.inactive);
      this.secondaryStickyElement.classList.add(this.classes.stickyHidden);
    }
  }

  deactivate() {
    // Reset all tracker classes and add inactive state for desktop
    this.resetStickyElement();
    this.stickyElement.classList.add(this.classes.inactive);
    
    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.classList.add(this.classes.inactive);
    }
  }

  resetStickyElement() {
    this.stickyElement.classList.remove(
      this.classes.stickyActive, 
      this.classes.stickyVisible, 
      this.classes.stickyHidden
    );

    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.classList.remove(
        this.classes.secondaryActive,
        this.classes.stickyVisible,
        this.classes.stickyHidden,
        this.classes.priceSticky // NEW: Remove price sticky class
      );
    }

    document.documentElement.style.removeProperty('--primary-sticky-height');
    this.currentStickyState = null;

    // NEW: Reset sticky detection
    this.resetStickyDetection();
  }

  updateSecondaryPosition() {
    if (!this.secondaryStickyElement || !this.isActive) return;

    // Use requestAnimationFrame to ensure accurate measurements
    requestAnimationFrame(() => {
      const primaryRect = this.stickyElement.getBoundingClientRect();
      const primaryHeight = primaryRect.height + this.secondarySpacing;
      
      document.documentElement.style.setProperty('--primary-sticky-height', `${primaryHeight}px`);
    });
  }

  getElementPosition() {
    const rect = this.trackedElement.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    if (rect.top - this.offset > windowHeight) return 'above';
    if (rect.bottom + this.offset < 0) return 'below';
    return 'visible';
  }

  updateState() {
    if (this.ticking || this.isResizing) return;

    this.ticking = true;
    requestAnimationFrame(() => {
      const position = this.getElementPosition();
      if (position !== this.lastPosition) {
        this.lastPosition = position;
        
        // Update main sticky behavior only if active (mobile)
        if (this.isActive) {
          this.toggleStickyPosition(position);
        }
      }
      
      // NEW: Check actual sticky state on both mobile and desktop
      this.checkActualStickyState();
      
      this.ticking = false;
    });
  }

  // NEW: Check if elements are physically stuck to screen
  checkActualStickyState() {
    // NEW: Run on both mobile and desktop
    // Get current positions of elements
    const primaryRect = this.stickyElement.getBoundingClientRect();
    const secondaryRect = this.secondaryStickyElement ? 
      this.secondaryStickyElement.getBoundingClientRect() : null;

    // Store previous positions (first run)
    if (this.lastPrimaryTop === null) {
      this.lastPrimaryTop = primaryRect.top;
      this.lastSecondaryTop = secondaryRect ? secondaryRect.top : 0.5;
    }

    // Detect when primary element reaches/passes top of viewport
    if (this.lastPrimaryTop > 0 && primaryRect.top <= 0 && !this.isPrimaryStuck) {
      this.isPrimaryStuck = true;
      this.onPrimaryStuck();
    } else if (this.lastPrimaryTop <= 0 && primaryRect.top > 0 && this.isPrimaryStuck) {
      this.isPrimaryStuck = false;
      this.onPrimaryUnstuck();
    }

    // Detect when secondary element should be "sticky" (works on both mobile and desktop)
    if (secondaryRect && this.secondaryStickyElement) {
      let shouldBeSecondaryStuck = false;
      
      if (this.isActive) {
        // Mobile: Check if secondary is stuck to primary element  
        const primaryHeight = primaryRect.height;
        const secondaryTarget = primaryHeight;
        shouldBeSecondaryStuck = secondaryRect.top <= secondaryTarget && primaryRect.top <= 0;
      } else {
        // Desktop: accurate sticky detection w/ non-zero top
        const stickyTop = this.getStickyTop(this.secondaryStickyElement);
        // Stuck when the element is pinned at its configured sticky top
        // (allow small epsilon for layout jitter).
        const epsilon = 6;
        shouldBeSecondaryStuck = secondaryRect.top <= (stickyTop + epsilon);
      }
      if (shouldBeSecondaryStuck && !this.isSecondaryStuck) {
        this.isSecondaryStuck = true;
        this.onSecondaryStuck();
      } else if (!shouldBeSecondaryStuck && this.isSecondaryStuck) {
        this.isSecondaryStuck = false;
        this.onSecondaryUnstuck();
      }
    }

    // Store current positions for next check
    this.lastPrimaryTop = primaryRect.top;
    this.lastSecondaryTop = secondaryRect ? secondaryRect.top : 0;
  }

  // NEW: Reset sticky detection state
  resetStickyDetection() {
    this.lastPrimaryTop = null;
    this.lastSecondaryTop = null;
    this.isPrimaryStuck = false;
    this.isSecondaryStuck = false;
    this.removePriceStickyClass();
  }

  // NEW: Sticky event handlers (work on both mobile and desktop)
  onPrimaryStuck() {
    // console.log('Primary element reached top of viewport');
    // Add your logic here
  }

  onPrimaryUnstuck() {
    // console.log('Primary element moved away from top of viewport');
    // Add your logic here
  }

  onSecondaryStuck() {
    // console.log('Secondary element physically stuck to screen');
    this.addPriceStickyClass();
  }

  onSecondaryUnstuck() {
    // console.log('Secondary element no longer physically stuck to screen');  
    this.removePriceStickyClass();
  }

  // NEW: Add price sticky class to secondary element
  addPriceStickyClass() {
    if (!this.secondaryStickyElement) return;

    this.secondaryStickyElement.classList.add(this.classes.priceSticky);
    // console.log('Price sticky class added');
  }

  // NEW: Remove price sticky class from secondary element
  removePriceStickyClass() {
    if (!this.secondaryStickyElement) return;

    this.secondaryStickyElement.classList.remove(this.classes.priceSticky);
    // console.log('Price sticky class removed');
  }

  toggleStickyPosition(position) {
    const shouldBeSticky = position === this.stickyState;
    
    if (shouldBeSticky && this.currentStickyState !== 'visible') {
      this.showStickyElements();
    } else if (!shouldBeSticky && this.currentStickyState !== 'hidden') {
      this.hideStickyElements();
    }
  }

  showStickyElements() {
    this.stickyElement.classList.remove(this.classes.stickyHidden);
    this.stickyElement.classList.add(this.classes.stickyActive, this.classes.stickyVisible);

    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.classList.remove(this.classes.stickyHidden);
      this.secondaryStickyElement.classList.add(this.classes.secondaryActive, this.classes.stickyVisible);
      
      setTimeout(() => this.updateSecondaryPosition(), 50);
    }
    
    this.currentStickyState = 'visible';
  }

  hideStickyElements() {
    this.stickyElement.classList.remove(this.classes.stickyVisible);
    this.stickyElement.classList.add(this.classes.stickyHidden);

    if (this.secondaryStickyElement) {
      this.secondaryStickyElement.classList.remove(this.classes.stickyVisible);
      this.secondaryStickyElement.classList.add(this.classes.stickyHidden);
      this.removePriceStickyClass();
    }

    this.currentStickyState = 'hidden';

    setTimeout(() => {
      if (this.stickyElement.classList.contains(this.classes.stickyHidden)) {
        this.stickyElement.classList.remove(this.classes.stickyActive);
        
        if (this.secondaryStickyElement) {
          this.secondaryStickyElement.classList.remove(this.classes.secondaryActive);
        }
        
        document.documentElement.style.removeProperty('--primary-sticky-height');
      }
    }, 300);
  }

  // Public API methods
  setStickyState(state) {
    if (['visible', 'above', 'below'].includes(state)) {
      this.stickyState = state;
      this.forceUpdateState();
    } else {
      console.error('Invalid state. Use "visible", "above", or "below".');
    }
  }

  setOffset(offset) {
    this.offset = offset;
    this.forceUpdateState();
  }

  setSecondarySpacing(spacing) {
    this.secondarySpacing = spacing;
    if (this.secondaryStickyElement && 
        this.secondaryStickyElement.classList.contains(this.classes.secondaryActive)) {
      this.updateSecondaryPosition();
    }
  }

  setMaxWidth(maxWidth) {
    this.maxWidth = maxWidth;
    this.checkViewportWidth();
  }

  setResizeDebounceDelay(delay) {
    this.resizeDebounceDelay = delay;
  }

  setClasses(newClasses) {
    this.classes = { ...this.classes, ...newClasses };
  }

  // NEW: Set custom price sticky class name
  setPriceStickyClass(className) {
    // Remove old class if secondary element has it
    if (this.secondaryStickyElement && 
        this.secondaryStickyElement.classList.contains(this.classes.priceSticky)) {
      this.secondaryStickyElement.classList.remove(this.classes.priceSticky);
      this.classes.priceSticky = className;
      this.secondaryStickyElement.classList.add(this.classes.priceSticky);
    } else {
      this.classes.priceSticky = className;
    }
  }

  // Force refresh - useful for manual updates
  refresh() {
    this.forceUpdateState();
  }

  destroy() {
    // Remove scroll handler
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);

    // Clear timeouts
    this.resizeTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.resizeTimeouts.clear();

    // Remove resize/orientation handlers
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleOrientationChange);
    if ('visualViewport' in window) {
      window.visualViewport.removeEventListener('resize', this.handleResize);
    }
    document.removeEventListener('click', this.handleLiveOptionsClick, true);
    if (this.liveOptionsClickTimeout) {
      clearTimeout(this.liveOptionsClickTimeout);
      this.liveOptionsClickTimeout = null;
    }

    // Disconnect observers and asset loader
    if (this.layoutRO) this.layoutRO.disconnect();
    if (this.assetLoadHandler) {
      document.removeEventListener('load', this.assetLoadHandler, true);
    }

    // Reset classes/styles
    this.resetStickyElement();
    document.documentElement.style.removeProperty('--resize-mode');
    this.resetStickyDetection();
  }
}

// Initialization
const productInfoElement = document.querySelector('product-info');
const productType =
  productInfoElement && productInfoElement.dataset
    ? (productInfoElement.dataset.productType || '').trim()
    : '';

if (productType === 'Config' || productType === 'Sauna') {
  const trackedElement = document.querySelector('.product-form__submit');
  const stickyElement = document.querySelector('.product__media-wrapper');

  if (trackedElement && stickyElement) {
    new ViewportTracker({
      trackedElement,
      stickyElement,
      secondaryStickyElement: document.querySelector('.product__price-wrapper'),
      initialStickyState: 'above',
      offset: -200,
      maxWidth: 749,
      secondarySpacing: 0,
      resizeDebounceDelay: 250,
      
      // CSS class names
      stickyActiveClass: 'product-sticky-active',
      stickyHiddenClass: 'product-sticky-hidden',
      stickyVisibleClass: 'product-sticky-visible',
      secondaryActiveClass: 'product-secondary-sticky',
      priceStickyClass: 'price-is-sticky', // NEW: Class applied when price is physically stuck
      inactiveClass: 'product-tracker-inactive',
      resizingClass: 'product-tracker-resizing'
    });
  }
} else if (productType === 'Hot Tub') {
  initPriceStickyOnly();
}

function initPriceStickyOnly() {
  const priceElement = document.querySelector('.product__price-wrapper');
  if (!priceElement) return;
  let isPriceStuck = false;

  const updatePriceStickyClass = () => {
    const rect = priceElement.getBoundingClientRect();
    const stickyTop = parseFloat(getComputedStyle(priceElement).top) || 0;
    const currentTop = Math.round(rect.top * 100) / 100;
    const enterThreshold = stickyTop + 0.5;
    const exitThreshold = stickyTop + 2.5;

    if (!isPriceStuck && currentTop <= enterThreshold) {
      isPriceStuck = true;
    } else if (isPriceStuck && currentTop > exitThreshold) {
      isPriceStuck = false;
    }

    priceElement.classList.toggle('price-is-sticky', isPriceStuck);
  };

  let ticking = false;
  const onScrollOrResize = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updatePriceStickyClass();
      ticking = false;
    });
  };

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
  if ('visualViewport' in window) {
    window.visualViewport.addEventListener('resize', onScrollOrResize);
  }

  updatePriceStickyClass();
}

// Note: On initial load:
// - Desktop (>749px): Elements get 'product-tracker-inactive' class and remain in natural state
// - Mobile (≤749px): Elements get 'product-sticky-hidden' class and are ready for sticky behavior  
// - 'price-is-sticky' class works on both mobile and desktop:
//   * Mobile: Applied when price physically sticks below primary element  
//   * Desktop: Applied when price physically sticks to top of viewport
